import { useCallback, useEffect, useRef, useState } from 'react';
import type { HexTileMapKey } from '@/game/hexmap';
import type { Command } from '@/game/sim';
import type { Frame, FrameStore } from '@/game/view/frame';
import {
  type SendTarget,
  type SentAttack,
  sendCommands,
  sentAttackPos,
  targetCell,
  updateSentAttacks,
} from '@/game/view/sendTroops';
import type { UiStore } from '../ui/store';

/**
 * Send troops (from a notification): the open picker's target, and the
 * attacks sent, each ringed on the map until it resolves. Selecting a unit or
 * building (their panels share the dock) closes the picker.
 */
export function useSendTroops(
  ui: UiStore,
  frames: FrameStore,
  frame: Frame,
  dispatch: (command: Command) => void,
  selection: { selectedUnitIds: readonly string[]; selectedBuildingId: string | null }
) {
  const [target, setTarget] = useState<SendTarget | null>(null);
  const [sent, setSent] = useState<SentAttack[]>([]);
  const sentIdRef = useRef(0);
  const { selectedUnitIds, selectedBuildingId } = selection;
  useEffect(() => {
    if (selectedUnitIds.length > 0 || selectedBuildingId !== null) setTarget(null);
  }, [selectedUnitIds, selectedBuildingId]);
  // Mark started attacks, drop resolved ones.
  useEffect(() => {
    setSent((list) => (list.length === 0 ? list : updateSentAttacks(frame.view, list)));
  }, [frame]);

  const isVisible = useCallback(
    (key: HexTileMapKey) => frames.get().fog.visible.has(key),
    [frames]
  );
  const open = useCallback(
    (to: SendTarget) => {
      setTarget(to);
      ui.set({ selectedUnitIds: [], selectedBuildingId: null, placementMode: null });
    },
    [ui]
  );
  const close = useCallback(() => setTarget(null), []);
  const send = useCallback(
    (unitIds: string[]) => {
      if (!target) return;
      const { view, playerId } = frames.get();
      const commands = sendCommands(view, playerId, unitIds, target, isVisible);
      for (const command of commands) dispatch(command);
      if (commands.length > 0) {
        const attack: SentAttack = {
          id: ++sentIdRef.current,
          target,
          cell: targetCell(view, target, isVisible),
          unitIds: [...unitIds],
          tick: view.tick,
        };
        setSent((list) => [...list, attack]);
      }
      setTarget(null);
    },
    [target, frames, isVisible, dispatch]
  );
  /** Where the picker's target is now (its unit's live tile while in sight). */
  const targetPos = target ? targetCell(frame.view, target, isVisible) : null;
  /** The rings: each attack's target while seen, else the tile sent to. */
  const rings = sent.map((a) => sentAttackPos(frame.view, a, isVisible));

  return { target, open, close, send, targetPos, rings };
}

export type SendTroops = ReturnType<typeof useSendTroops>;
